import type { ElementRef } from '@angular/core';
import { computed, inject, output, viewChild, input, Component } from '@angular/core';

import { BREAKPOINT_MOBILE, BREAKPOINT_TABLET } from '@shared/constants/breakpoints';
import { LazyViewportDirective } from '@shared/directives';
import { BreakpointService } from '@shared/services';

/**
 * Per-breakpoint video sources. Each entry is used at or below its breakpoint's width; the base
 * `videoSrc` covers anything larger. Ordered narrowest-first so the smallest match wins.
 */
export interface ResponsiveVideoSources {
  /** Played at or below the mobile breakpoint (≤600px). */
  mobile?: string;
  /** Played at or below the tablet breakpoint (≤768px). */
  tablet?: string;
}

@Component({
  selector: 'app-video-player',
  imports: [LazyViewportDirective],
  templateUrl: './video-player.html',
  styleUrl: './video-player.scss',
})
export class VideoPlayerComponent {
  private readonly breakpoints = inject(BreakpointService);

  /** Breakpoints from narrowest to widest, mapped to their max-width in px. */
  private readonly breakpointOrder = [
    ['mobile', BREAKPOINT_MOBILE],
    ['tablet', BREAKPOINT_TABLET],
  ] as const satisfies readonly [keyof ResponsiveVideoSources, number][];

  videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  /** Base source, used on desktop and as the fallback when no smaller `sources` entry matches. */
  videoSrc = input.required<string>();

  /** Optional lighter/art-directed sources for smaller screens (e.g. `{ mobile, tablet }`). */
  sources = input<ResponsiveVideoSources>();

  videoLabel = input<string>();
  poster = input<string>();
  muted = input<boolean>(true);
  autoplay = input<boolean>(true);

  /**
   * The source actually attached to the element. The directive sets `element.src` directly, so
   * native `<source media>` switching would be bypassed; we resolve the responsive source here
   * instead. Runs in the browser only — SSG never attaches bytes, so the base src is a safe default.
   */
  resolvedSrc = computed(() => {
    const sources = this.sources();

    // Narrowest-first: the smallest *provided* source whose max-width matches wins. A narrower
    // breakpoint with no source cascades up to the next provided one (e.g. a tablet-only source
    // still covers mobile) before falling back to the base `videoSrc`.
    if (sources) {
      for (const [key, maxWidth] of this.breakpointOrder) {
        const src = sources[key];

        if (src && this.breakpoints.matches(`(max-width: ${maxWidth}px)`)) {
          return src;
        }
      }
    }

    return this.videoSrc();
  });

  /** See `LazyViewportDirective.lazyStrategy`. Use `idle` for above-the-fold video. */
  lazyStrategy = input<'viewport' | 'idle'>('viewport');

  videoEntered = output<HTMLVideoElement | undefined>();

  /** Called by LazyViewportDirective once a card's video is in view and its source is attached. */
  onVideoEntered(): void {
    const video = this.videoPlayer();

    this.videoEntered.emit(video?.nativeElement);

    if (this.autoplay()) {
      this.play();
    }

    if (this.poster() && video?.nativeElement) {
      video.nativeElement.poster = this.poster() as string;
    }
  }

  play(): void {
    const video = this.videoPlayer();

    if (video?.nativeElement) {
      if (this.muted()) {
        video.nativeElement.muted = true;
      }

      video.nativeElement.play().catch(() => {
        // Playback can be interrupted when switching cards quickly; safe to ignore.
      });
    }
  }

  pause(): void {
    const video = this.videoPlayer();

    if (video?.nativeElement) {
      video.nativeElement.pause();
    }
  }

  setTime(time: number): void {
    const video = this.videoPlayer();

    if (video?.nativeElement) {
      video.nativeElement.currentTime = time;
    }
  }
}
